import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { PageHeader } from '@/shared/ui/layout/PageHeader';
import { Spinner } from '@/shared/ui/feedback/Spinner';
import { EmptyState } from '@/shared/ui/feedback/EmptyState';
import { ErrorState } from '@/shared/ui/feedback/ErrorState';
import { RotateCcwIcon, ChevronRightIcon } from '@/shared/ui/icons';
import { extractErrorMessage } from '@/shared/api/errors';
import { useParcelsAvailableForReturn } from '@/entities/returns';
import { usePricingConfig } from '@/entities/pricing';
import { formatUsd } from '@/shared/utils/currency';

export const WidgetsServicesReturnListMain = () => {
  const { t } = useTranslation('services');
  const navigate = useNavigate();

  const { data, isLoading, isError, error, refetch } = useParcelsAvailableForReturn();
  const parcels = data ?? [];

  const { data: pricing } = usePricingConfig();
  const fee = pricing?.return_fee;

  return (
    <>
      <PageHeader title={t('returnTitle')} showBack backTo="/settings/services" />
      <div className="flex flex-col gap-3 p-4 md:mx-auto md:w-full md:max-w-2xl lg:p-6">
        {fee != null && (
          <div className="flex items-center justify-between rounded-2xl border border-slate-200 bg-white p-4 shadow-card">
            <span className="text-sm font-medium text-ink-muted">{t('returnFeeLabel')}</span>
            <span className="text-base font-semibold text-ink">{formatUsd(Number(fee))}</span>
          </div>
        )}

        {isLoading ? (
          <div className="flex justify-center py-16">
            <Spinner size="lg" />
          </div>
        ) : isError ? (
          <ErrorState message={extractErrorMessage(error)} onRetry={() => void refetch()} />
        ) : parcels.length === 0 ? (
          <EmptyState
            icon={<RotateCcwIcon className="h-10 w-10" aria-hidden />}
            title={t('noParcelsAvailable')}
          />
        ) : (
          <>
            <p className="text-sm font-medium text-ink-muted">{t('selectParcel')}</p>
            {parcels.map((parcel) => (
              <button
                key={parcel.id}
                type="button"
                onClick={() => navigate(`/services/return/${parcel.id}`)}
                className="flex w-full items-center gap-3 rounded-2xl border border-slate-200 bg-white p-4 text-left shadow-card transition-colors hover:bg-slate-50 active:bg-slate-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/40"
              >
                <div className="min-w-0 flex-1">
                  <p className="truncate text-base font-semibold text-ink">{parcel.name}</p>
                  <p className="truncate text-xs text-ink-muted">{parcel.trek_id}</p>
                  <span className="mt-1.5 inline-flex rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-ink-muted">
                    {t(`parcelStatus:${parcel.status}`)}
                  </span>
                </div>
                <ChevronRightIcon className="h-5 w-5 shrink-0 text-ink-faint" aria-hidden />
              </button>
            ))}
          </>
        )}
      </div>
    </>
  );
};
